import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, Users, Award, Target, Code, Lock, ShieldCheck } from "lucide-react";
import { Helmet } from "@/components/Helmet";

const About = () => {
  const values = [
    { icon: Shield, title: "Security First", description: "Risk reduction and human validation are our priorities" },
    { icon: Users, title: "Client Focused", description: "We help you understand risk and act with confidence" },
    { icon: Award, title: "Expert Team", description: "Years of WordPress security experience" },
  ];

  const team = [
    { 
      name: "William", 
      role: "Founder & Security Specialist",
      description: "With years of experience in WordPress security, William founded ShadowScan to make managed security accessible to businesses of all sizes."
    },
    { 
      name: "Bryan Bridle", 
      role: "Website Security Consultant",
      description: "Expert in security strategy and risk assessment, helping clients understand and reduce risk to their digital assets."
    },
    { 
      name: "Anish Poudel", 
      role: "Senior Security Engineer",
      description: "Specializes in detection engineering and security architecture, leading our technical security initiatives."
    },
    { 
      name: "Niraj Thapa", 
      role: "Security Engineer",
      description: "Focused on vulnerability assessment and testing, ensuring comprehensive coverage for client websites."
    },
    { 
      name: "Alex Pike", 
      role: "Business and Functionality Manager",
      description: "Leads day-to-day portal and service operations, aligning customer onboarding, reporting, and support with our managed security workflows."
    },
  ];

  const getIcon = (role: string) => {
    if (role.includes('Founder')) return Target;
    if (role.includes('Senior')) return ShieldCheck;
    if (role.includes('Consultant')) return Code;
    return Lock;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>About ShadowScan | Managed WordPress Security Team</title>
        <meta
          name="description"
          content="Meet ShadowScan's Australian security team with 10+ years of hands-on experience delivering continuous monitoring, rapid response, and clear guidance."
        />
      </Helmet>
      <Navigation />
      <main className="flex-grow pt-24">
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// ABOUT_US</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">Meet <span className="text-gradient">ShadowScan</span></h1>
              <p className="text-lg text-muted-foreground leading-relaxed">WordPress security experts who help businesses reduce risk with managed oversight</p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto space-y-6 text-muted-foreground leading-relaxed">
              <p>ShadowScan is run by WordPress security experts who are passionate about reducing risk for businesses online. We understand that not everyone is a tech expert, and that's exactly why we're here.</p>
              <p>Our mission is simple: make managed WordPress security accessible and understandable for everyone. We take complex security concepts and break them down into clear, actionable steps.</p>
              <p>When you work with ShadowScan, you're not just getting a scan - you're getting a trusted partner who validates alerts, coordinates response, and supports recovery.</p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <div className="font-mono text-sm text-primary mb-4">// OUR_VALUES</div>
              <h2 className="text-3xl font-heading font-bold">What Drives Us</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <Card key={index} className="cyber-card text-center hover:border-primary/50 transition-all">
                    <CardContent className="pt-8 pb-6">
                      <div className="w-14 h-14 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-7 w-7 text-primary" />
                      </div>
                      <h3 className="font-heading font-semibold mb-2">{value.title}</h3>
                      <p className="text-sm text-muted-foreground">{value.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <div className="font-mono text-sm text-primary mb-4">// OUR_TEAM</div>
              <h2 className="text-3xl font-heading font-bold">Security Experts</h2>
              <p className="text-muted-foreground mt-2">Real humans, real oversight</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {team.map((member, index) => {
                const Icon = getIcon(member.role);
                return (
                  <Card key={index} className="cyber-card text-center border-primary/30 hover:border-primary/50 transition-all">
                    <CardContent className="pt-8 space-y-4">
                      <div className="w-20 h-20 bg-primary/10 border border-primary/30 rounded-full flex items-center justify-center mx-auto">
                        <Icon className="h-10 w-10 text-primary" />
                      </div>
                      <h3 className="text-xl font-heading font-bold">{member.name}</h3>
                      <p className="text-primary font-mono text-sm">{member.role}</p>
                      <p className="text-muted-foreground text-sm">{member.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default About;
